import React, { useState, useEffect, useCallback } from 'react';
import { X, MapPin, Clock, Users, User, Phone, CreditCard, Banknote, Smartphone, Loader2, CheckCircle, Navigation } from 'lucide-react';
import GooglePlacesAutocomplete from './GooglePlacesAutocomplete';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const OnlineOrderModal = ({ isOpen, onClose, defaultPickupAddress = '' }) => {
  const [pickupAddress, setPickupAddress] = useState(defaultPickupAddress);
  const [destinationAddress, setDestinationAddress] = useState('');
  const [pickupDatetime, setPickupDatetime] = useState('now');
  const [customDate, setCustomDate] = useState('');
  const [customTime, setCustomTime] = useState('');
  const [persons, setPersons] = useState(1);
  const [customerName, setCustomerName] = useState('');
  const [customerPhone, setCustomerPhone] = useState('');
  const [paymentMethod, setPaymentMethod] = useState('bar');
  const [estimatedPrice, setEstimatedPrice] = useState(null);
  const [distanceKm, setDistanceKm] = useState(null);
  const [isCalculating, setIsCalculating] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [orderResult, setOrderResult] = useState(null);
  const [error, setError] = useState('');

  // Reset form when modal opens
  useEffect(() => {
    if (isOpen) {
      setOrderResult(null);
      setError('');
      setPickupAddress(defaultPickupAddress);
      setDestinationAddress('');
      setEstimatedPrice(null);
      setDistanceKm(null);
    }
  }, [isOpen, defaultPickupAddress]);

  // Calculate price when both addresses are set
  const calculatePrice = useCallback(async () => {
    if (!pickupAddress || !destinationAddress) return;
    setIsCalculating(true);
    setError('');
    try {
      const res = await fetch(`${API_URL}/api/calculate-price`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ origin: pickupAddress, destination: destinationAddress }),
      });
      if (!res.ok) throw new Error('Preisberechnung fehlgeschlagen');
      const data = await res.json();
      setEstimatedPrice(data.total_fare);
      setDistanceKm(data.distance_km);
    } catch (e) {
      setEstimatedPrice(null);
      setDistanceKm(null);
    }
    setIsCalculating(false);
  }, [pickupAddress, destinationAddress]);

  useEffect(() => {
    if (pickupAddress && destinationAddress) {
      const timer = setTimeout(calculatePrice, 500);
      return () => clearTimeout(timer);
    }
  }, [pickupAddress, destinationAddress, calculatePrice]);

  const handleSubmit = async () => {
    if (!pickupAddress || !destinationAddress || !customerName || !customerPhone) {
      setError('Bitte füllen Sie alle Pflichtfelder aus.');
      return;
    }

    setIsSubmitting(true);
    setError('');

    // Build pickup datetime
    let pickupDt = 'now';
    if (pickupDatetime !== 'now' && customDate && customTime) {
      pickupDt = `${customDate}T${customTime}:00`;
    }

    // Try to get geolocation (one-time, non-blocking)
    let lat = null;
    let lng = null;
    try {
      if (navigator.geolocation) {
        const pos = await new Promise((resolve, reject) => {
          navigator.geolocation.getCurrentPosition(resolve, reject, {
            enableHighAccuracy: true, timeout: 5000, maximumAge: 300000
          });
        });
        lat = pos.coords.latitude;
        lng = pos.coords.longitude;
      }
    } catch (e) {
      // User denied or error – continue without coordinates
    }

    try {
      const res = await fetch(`${API_URL}/api/orders`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          customer_name: customerName,
          customer_phone: customerPhone,
          pickup_address: pickupAddress,
          destination_address: destinationAddress,
          pickup_datetime: pickupDt,
          persons,
          payment_method: paymentMethod,
          estimated_price: estimatedPrice,
          distance_km: distanceKm,
          customer_lat: lat,
          customer_lng: lng,
        }),
      });
      if (!res.ok) throw new Error('Bestellung fehlgeschlagen');
      const data = await res.json();
      setOrderResult(data);
      // Open WhatsApp
      window.open(data.whatsapp_url, '_blank');
    } catch (e) {
      setError('Bestellung konnte nicht gesendet werden. Bitte versuchen Sie es erneut.');
    }
    setIsSubmitting(false);
  };

  if (!isOpen) return null;

  // Success screen
  if (orderResult) {
    return (
      <div className="fixed inset-0 bg-black/70 z-50 flex items-center justify-center p-4" onClick={onClose}>
        <div className="bg-gray-900 rounded-2xl max-w-md w-full p-6 border border-gray-700" onClick={e => e.stopPropagation()} data-testid="order-success-modal">
          <div className="text-center">
            <div className="w-16 h-16 bg-green-600 rounded-full flex items-center justify-center mx-auto mb-4">
              <CheckCircle className="w-10 h-10 text-white" />
            </div>
            <h2 className="text-2xl font-bold text-white mb-2">Anfrage erhalten!</h2>
            <p className="text-gray-300 mb-1">Bestell-Nr: <span className="text-yellow-500 font-bold">{orderResult.order_id}</span></p>
            <div className="bg-yellow-600/20 border border-yellow-600/40 rounded-lg p-3 my-4">
              <p className="text-yellow-400 font-semibold text-sm">Wir prüfen Ihre Anfrage und bestätigen sie in Kürze.</p>
            </div>
            <div className="text-left bg-gray-800 rounded-lg p-4 mb-4 text-sm text-gray-300 space-y-1">
              <p><span className="text-gray-500">Von:</span> {orderResult.pickup_address}</p>
              <p><span className="text-gray-500">Nach:</span> {orderResult.destination_address}</p>
              <p><span className="text-gray-500">Zeit:</span> {orderResult.pickup_datetime}</p>
              <p><span className="text-gray-500">Personen:</span> {orderResult.persons}</p>
              {orderResult.estimated_price && <p><span className="text-gray-500">Preis:</span> CHF {orderResult.estimated_price.toFixed(2)}</p>}
              <p><span className="text-gray-500">Zahlung:</span> {orderResult.payment_method}</p>
            </div>
            <a
              href={`/bestellstatus/${orderResult.order_id}`}
              className="block w-full bg-gray-800 hover:bg-gray-700 text-white py-3 rounded-lg font-semibold mb-3 text-center transition-colors"
              data-testid="order-status-link"
            >
              Status prüfen
            </a>
            <button
              onClick={onClose}
              className="w-full bg-yellow-600 hover:bg-yellow-700 text-white py-3 rounded-lg font-semibold transition-colors"
              data-testid="order-close-btn"
            >
              Schliessen
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="fixed inset-0 bg-black/70 z-50 flex items-start justify-center p-4 overflow-y-auto" onClick={onClose}>
      <div className="bg-gray-900 rounded-2xl max-w-lg w-full my-8 border border-gray-700" onClick={e => e.stopPropagation()} data-testid="online-order-modal">
        {/* Header */}
        <div className="flex items-center justify-between p-5 border-b border-gray-700">
          <h2 className="text-xl font-bold text-white">Online bestellen</h2>
          <button onClick={onClose} className="text-gray-400 hover:text-white p-1" data-testid="order-modal-close">
            <X className="w-6 h-6" />
          </button>
        </div>

        <div className="p-5 space-y-4">
          {/* Pickup Address */}
          <div>
            <label className="text-sm font-medium text-gray-300 mb-1 flex items-center">
              <MapPin className="w-4 h-4 mr-1 text-green-500" /> Abholadresse *
            </label>
            <GooglePlacesAutocomplete
              placeholder="Abholadresse eingeben..."
              showLocationButton={true}
              locationButtonText="Mein Standort"
              initialValue={defaultPickupAddress}
              onAddressSelect={(data) => setPickupAddress(data.formatted_address)}
              className="order-autocomplete"
            />
          </div>

          {/* Destination Address */}
          <div>
            <label className="text-sm font-medium text-gray-300 mb-1 flex items-center">
              <MapPin className="w-4 h-4 mr-1 text-red-500" /> Zieladresse *
            </label>
            <GooglePlacesAutocomplete
              placeholder="Zieladresse eingeben..."
              onAddressSelect={(data) => setDestinationAddress(data.formatted_address)}
              className="order-autocomplete"
            />
          </div>

          {/* Live Price */}
          {(isCalculating || estimatedPrice) && (
            <div className="bg-gray-800 rounded-lg p-3 flex items-center justify-between" data-testid="order-price-display">
              <span className="text-gray-300 text-sm">Geschätzter Preis</span>
              {isCalculating ? (
                <Loader2 className="w-5 h-5 animate-spin text-yellow-500" />
              ) : (
                <div className="text-right">
                  <span className="text-yellow-500 text-xl font-bold">CHF {estimatedPrice?.toFixed(2)}</span>
                  {distanceKm && <span className="text-gray-500 text-xs block">{distanceKm.toFixed(1)} km</span>}
                </div>
              )}
            </div>
          )}

          {/* Date & Time */}
          <div>
            <label className="text-sm font-medium text-gray-300 mb-1 flex items-center">
              <Clock className="w-4 h-4 mr-1 text-yellow-500" /> Datum & Uhrzeit
            </label>
            <div className="flex gap-2">
              <button
                onClick={() => setPickupDatetime('now')}
                className={`flex-1 py-2 px-3 rounded-lg text-sm font-semibold transition-colors ${pickupDatetime === 'now' ? 'bg-yellow-600 text-white' : 'bg-gray-800 text-gray-300 hover:bg-gray-700'}`}
                data-testid="order-now-btn"
              >
                Jetzt
              </button>
              <button
                onClick={() => setPickupDatetime('custom')}
                className={`flex-1 py-2 px-3 rounded-lg text-sm font-semibold transition-colors ${pickupDatetime === 'custom' ? 'bg-yellow-600 text-white' : 'bg-gray-800 text-gray-300 hover:bg-gray-700'}`}
                data-testid="order-custom-time-btn"
              >
                Termin wählen
              </button>
            </div>
            {pickupDatetime === 'custom' && (
              <div className="flex gap-2 mt-2">
                <input
                  type="date"
                  value={customDate}
                  onChange={e => setCustomDate(e.target.value)}
                  className="flex-1 bg-gray-800 border border-gray-600 text-white rounded-lg px-3 py-2 text-sm"
                  data-testid="order-date-input"
                />
                <input
                  type="time"
                  value={customTime}
                  onChange={e => setCustomTime(e.target.value)}
                  className="flex-1 bg-gray-800 border border-gray-600 text-white rounded-lg px-3 py-2 text-sm"
                  data-testid="order-time-input"
                />
              </div>
            )}
          </div>

          {/* Persons */}
          <div>
            <label className="text-sm font-medium text-gray-300 mb-1 flex items-center">
              <Users className="w-4 h-4 mr-1 text-blue-400" /> Anzahl Personen
            </label>
            <div className="flex gap-1">
              {[1,2,3,4,5,6,7,8].map(n => (
                <button
                  key={n}
                  onClick={() => setPersons(n)}
                  className={`flex-1 py-2 rounded-lg text-sm font-semibold transition-colors ${persons === n ? 'bg-yellow-600 text-white' : 'bg-gray-800 text-gray-300 hover:bg-gray-700'}`}
                  data-testid={`order-persons-${n}`}
                >
                  {n}
                </button>
              ))}
            </div>
          </div>

          {/* Name & Phone */}
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="text-sm font-medium text-gray-300 mb-1 flex items-center">
                <User className="w-4 h-4 mr-1" /> Name *
              </label>
              <input
                type="text"
                value={customerName}
                onChange={e => setCustomerName(e.target.value)}
                placeholder="Ihr Name"
                className="w-full bg-gray-800 border border-gray-600 text-white rounded-lg px-3 py-2.5 text-sm focus:border-yellow-500 focus:ring-1 focus:ring-yellow-500"
                data-testid="order-name-input"
              />
            </div>
            <div>
              <label className="text-sm font-medium text-gray-300 mb-1 flex items-center">
                <Phone className="w-4 h-4 mr-1" /> Telefon *
              </label>
              <input
                type="tel"
                value={customerPhone}
                onChange={e => setCustomerPhone(e.target.value)}
                placeholder="+41 79 ..."
                className="w-full bg-gray-800 border border-gray-600 text-white rounded-lg px-3 py-2.5 text-sm focus:border-yellow-500 focus:ring-1 focus:ring-yellow-500"
                data-testid="order-phone-input"
              />
            </div>
          </div>

          {/* Payment Method */}
          <div>
            <label className="text-sm font-medium text-gray-300 mb-1">Zahlungsart</label>
            <div className="flex gap-2">
              {[
                { value: 'bar', label: 'Bar', icon: Banknote },
                { value: 'karte', label: 'Karte', icon: CreditCard },
                { value: 'twint', label: 'TWINT', icon: Smartphone },
              ].map(({ value, label, icon: Icon }) => (
                <button
                  key={value}
                  onClick={() => setPaymentMethod(value)}
                  className={`flex-1 flex items-center justify-center gap-1.5 py-2.5 rounded-lg text-sm font-semibold transition-colors ${paymentMethod === value ? 'bg-yellow-600 text-white' : 'bg-gray-800 text-gray-300 hover:bg-gray-700'}`}
                  data-testid={`order-pay-${value}`}
                >
                  <Icon className="w-4 h-4" />
                  {label}
                </button>
              ))}
            </div>
            <p className="text-xs text-gray-500 mt-1">Bezahlung erfolgt im Fahrzeug beim Fahrer.</p>
          </div>

          {/* Error */}
          {error && <p className="text-red-400 text-sm" data-testid="order-error">{error}</p>}

          {/* Submit */}
          <button
            onClick={handleSubmit}
            disabled={isSubmitting || !pickupAddress || !destinationAddress || !customerName || !customerPhone}
            className="w-full bg-yellow-600 hover:bg-yellow-700 disabled:bg-gray-700 disabled:text-gray-500 text-white py-3.5 rounded-lg font-bold text-lg transition-colors flex items-center justify-center gap-2"
            data-testid="order-submit-btn"
          >
            {isSubmitting ? (
              <><Loader2 className="w-5 h-5 animate-spin" /> Wird gesendet...</>
            ) : (
              'Bestellung senden'
            )}
          </button>
        </div>
      </div>
    </div>
  );
};

export default OnlineOrderModal;
